import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { usePortalAuth } from '../../context/PortalAuthContext';

function CustomerLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading: authLoading } = usePortalAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Set when this email and password open an account at more than one company.
  const [companies, setCompanies] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal');
    }
  }, [isAuthenticated, navigate]);

  // Show message from query params (e.g., after password reset)
  const message = searchParams.get('message');

  const submit = async (companyId) => {
    setError('');
    setLoading(true);

    const result = await login(email, password, companyId);

    if (result.success) {
      navigate('/portal');
    } else if (result.chooseCompany) {
      setCompanies(result.companies);
    } else {
      setCompanies(null);
      setError(result.error);
    }
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCompanies(null);
    submit(null);
  };

  if (authLoading) {
    return (
      <div className="portal-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="portal-auth-page">
      <div className="portal-auth-container">
        <div className="portal-auth-header">
          <h1>Customer Portal</h1>
          <p>Sign in to view your services and invoices</p>
        </div>

        {message && (
          <div className="portal-message success">
            {message}
          </div>
        )}

        {error && (
          <div className="portal-message error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="portal-auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
            />
          </div>

          {!companies && (
            <button type="submit" className="portal-btn-primary" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          )}
        </form>

        {companies && (
          <div className="portal-company-choice" role="group" aria-labelledby="portal-company-choice-title">
            <p id="portal-company-choice-title" className="portal-company-choice-title">
              You have an account with more than one company. Which one do you want to sign in to?
            </p>
            {companies.map((c) => (
              <button
                key={c.id}
                type="button"
                className="portal-btn-primary portal-company-option"
                disabled={loading}
                onClick={() => submit(c.id)}
              >
                {c.name}
              </button>
            ))}
            <button type="button" className="portal-company-back" onClick={() => setCompanies(null)}>
              Use a different email
            </button>
          </div>
        )}

        <div className="portal-auth-links">
          <Link to="/portal/forgot-password">Forgot your password?</Link>
        </div>
      </div>

      <style>{`
        .portal-company-choice { margin-top: 8px; }
        .portal-company-choice-title { font-weight: 500; margin: 0 0 12px; }
        .portal-company-option { margin-bottom: 10px; }
        .portal-company-back {
          background: none; border: none; color: #2563eb; padding: 8px 0;
          cursor: pointer; font-size: 0.9rem; width: 100%;
        }
        .portal-auth-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          padding: 20px;
        }
        .portal-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
        }
        .portal-loading .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255,255,255,0.2);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .portal-auth-container {
          background: white;
          border-radius: 16px;
          padding: 40px;
          width: 100%;
          max-width: 400px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        .portal-auth-header {
          text-align: center;
          margin-bottom: 32px;
        }
        .portal-auth-header h1 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0 0 8px 0;
        }
        .portal-auth-header p {
          color: #64748b;
          margin: 0;
          font-size: 0.95rem;
        }
        .portal-message {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 24px;
          font-size: 0.9rem;
        }
        .portal-message.success {
          background: #dcfce7;
          color: #15803d;
          border: 1px solid #bbf7d0;
        }
        .portal-message.error {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }
        .portal-auth-form .form-group {
          margin-bottom: 20px;
        }
        .portal-auth-form label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 6px;
        }
        .portal-auth-form input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.15s, box-shadow 0.15s;
          box-sizing: border-box;
        }
        .portal-auth-form input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
        .portal-btn-primary {
          width: 100%;
          padding: 14px 20px;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }
        .portal-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
        }
        .portal-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .portal-auth-links {
          text-align: center;
          margin-top: 24px;
        }
        .portal-auth-links a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 0.9rem;
        }
        .portal-auth-links a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}

export default CustomerLogin;
